<template lang="pug">
.selection
    .empty(v-if="!selectedField") {{ $t(`${baseTranslationPath}.select_field_hint`) }}
    template(v-else)
        .sel-header
            .sel-heading {{ $t(`${baseTranslationPath}.selected_field`) }}
            .sel-field
                .sel-index(v-if="fieldIndex") {{ fieldIndex }}
                font-awesome-icon.sel-icon(:icon="fieldIcon")
                .sel-field-info
                    .sel-field-name {{ selectedField.field_name || selectedField.label || selectedField.field_key }}
                    .sel-field-sub {{ $t(`${baseTranslationPath}.detected_as`) }} {{ selectedField.field_type }}
                .sel-saving(v-if="saving")
                    font-awesome-icon(:icon="['fas', 'spinner']" spin)
                    span {{ $t(`${baseTranslationPath}.saving`) }}
        .field-selection
            .add-entity
                aiq-select(
                    filterable
                    size="small"
                    :model-value="null"
                    :placeholder="$t(`${baseTranslationPath}.add_entity_ph`)"
                    no-data-text="None available"
                    @change="addEntity"
                )
                    aiq-option(
                        v-for="entity in entityOptions"
                        :key="entity"
                        :value="entity"
                        :label="entity"
                    )
            .mapping-hint {{ $t(`${baseTranslationPath}.mapping_order_hint`) }}
            .empty(v-if="!mappings.length") {{ $t(`${baseTranslationPath}.no_mappings`) }}
            draggable.map-list(
                v-else
                :list="mappings"
                item-key="id"
                handle=".drag-handle"
                @change="persist"
            )
                template(#item="{ element, index }")
                    .map-item(:class="{ 'is-shared': sharedWith(element.label).length }")
                        font-awesome-icon.drag-handle(:icon="['fas', 'grip-vertical']")
                        .order-index {{ index + 1 }}
                        .map-body
                            .field-name {{ element.label }}
                            .shared-note(v-if="sharedWith(element.label).length") {{ $t(`${baseTranslationPath}.also_mapped_to`, { fields: sharedWith(element.label).join(', ') }) }}
                        fa-icon.remove-icon(icon="fas fa-xmark" @click="remove(element)")
        .suggested(v-if="suggestedKeys.length")
            .sug-title {{ $t(`${baseTranslationPath}.suggested`) }}
            .sug-item(v-for="(key, index) in suggestedKeys" :key="`sug-${index}`")
                .order-index.muted {{ index + 1 }}
                .field-name {{ key }}
                aiq-button.accept-btn(
                    v-if="!isMapped(key)"
                    link
                    type="primary"
                    size="small"
                    @click="acceptSuggestion(key)"
                ) {{ $t(`${baseTranslationPath}.accept_suggestion`) }}
                .accepted-note(v-else) {{ $t(`${baseTranslationPath}.mapped`) }}
</template>
<script>
import { createNamespacedHelpers } from 'vuex';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import { fieldIconTypes } from './fieldUtil';

const { mapGetters } = createNamespacedHelpers('entities');

export default {
  name: 'Selection',
  props: {
    // form_fields row (has mapped_keys / suggested_keys), or null.
    selectedField: {
      type: Object,
      default: null,
    },
    formId: {
      type: [String, Number],
      default: null,
    },
    // 1-based index of the selected field within the form's field list.
    fieldIndex: {
      type: Number,
      default: null,
    },
    // all form_fields rows for the form, used to warn when an entity is already
    // mapped to another field.
    fields: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['updated'],
  data() {
    return {
      baseTranslationPath: 'iq_tools_tab.forms_tab.manage',
      mappings: [],
      original: [],
      saving: false,
    };
  },
  computed: {
    ...mapGetters(['selectionList']),
    fieldIcon() {
      return fieldIconTypes(get(this.selectedField, 'field_type')).icon;
    },
    // Entity source paths available to add. Shows ALL selectable entities,
    // including ones already mapped to this field (duplicate adds are a no-op).
    entityOptions() {
      return this.selectionList
        .map(entity => entity.name)
        .filter(Boolean);
    },
    // Map of entity source_path -> display names of OTHER fields in this form
    // that also map it. The same value may intentionally fill several fields,
    // so this only drives a warning, it does not block the mapping.
    otherFieldUsage() {
      const currentId = get(this.selectedField, 'id');
      const usage = {};
      (this.fields || []).forEach((field) => {
        if (!field || field.id === currentId) {
          return;
        }
        const label = get(field, 'field_name') || get(field, 'label') || get(field, 'field_key');
        if (!label) {
          return;
        }
        const keys = (get(field, 'mapped_keys', []) || [])
          .map(item => (typeof item === 'string' ? item : get(item, 'source_path', String(item))));
        keys.forEach((key) => {
          if (!key) {
            return;
          }
          if (!usage[key]) {
            usage[key] = [];
          }
          if (!usage[key].includes(label)) {
            usage[key].push(label);
          }
        });
      });
      return usage;
    },
    // Matcher suggestions, shown read-only for reference.
    suggestedKeys() {
      const suggested = get(this.selectedField, 'suggested_keys', []) || [];
      return suggested.map(item => (typeof item === 'string' ? item : get(item, 'source_path', String(item))));
    },
    // the current ordered list of source paths (what we persist as mapped_keys).
    orderedKeys() {
      return this.mappings.map(m => m.label);
    },
    dirty() {
      return !isEqual(this.orderedKeys, this.original);
    },
  },
  watch: {
    selectedField: {
      immediate: true,
      deep: true,
      handler(next) {
        this.load(next);
      },
    },
  },
  mounted() {
    // Populate the entity selector; safe to re-dispatch (cached in the store).
    this.$store.dispatch('entities/getEntitiesList');
  },
  methods: {
    load(field) {
      // Persisted mappings are authoritative; suggestions stay read-only.
      const mapped = get(field, 'mapped_keys', []) || [];
      const keys = mapped.map(item => (typeof item === 'string' ? item : get(item, 'source_path', String(item))));
      this.mappings = keys.map((label, id) => ({ label, id }));
      this.original = [...keys];
    },
    // Other fields in this form that also map the given entity source path.
    sharedWith(name) {
      return this.otherFieldUsage[name] || [];
    },
    // Whether a suggested source path is already in the mapped list.
    isMapped(name) {
      return this.mappings.some(m => m.label === name);
    },
    // Promote a matcher suggestion into the persisted mapping list.
    acceptSuggestion(name) {
      this.addEntity(name);
    },
    addEntity(name) {
      if (!name || this.mappings.some(m => m.label === name)) {
        return;
      }
      const id = this.mappings.reduce((max, m) => Math.max(max, m.id), -1) + 1;
      this.mappings = [...this.mappings, { label: name, id }];
      const others = this.sharedWith(name);
      if (others.length) {
        this.$aiq.notify.info(
          this.$t(`${this.baseTranslationPath}.entity_mapped_elsewhere`, { fields: others.join(', ') }),
        );
      }
      this.persist();
    },
    remove(item) {
      this.mappings = this.mappings.filter(f => f.id !== item.id);
      this.persist();
    },
    async persist() {
      if (!this.selectedField || !this.formId || !this.dirty) {
        return;
      }
      this.saving = true;
      try {
        await this.$store.dispatch('agentForms/updateFormField', {
          formId: this.formId,
          fieldId: this.selectedField.id,
          mapped_keys: this.orderedKeys,
        });
        this.original = [...this.orderedKeys];
        this.$emit('updated');
      } catch (_) {
        this.$aiq.notify.error(this.$t(`${this.baseTranslationPath}.mapping_save_error`));
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
<style lang="scss" scoped>
@import 'src/styles/aiq-variables.scss';

.selection {
  padding: 14px;
  height: 100%;
  overflow-y: auto;
}

.sel-header {
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid $aiq-tab-content-border;
}

.sel-heading {
  font-size: 16px;
  font-weight: 700;
  margin-bottom: 10px;
}

.sel-field {
  display: flex;
  align-items: center;
  gap: 10px;
}

.sel-icon {
  color: $aiq-input-border-hover;
  font-size: 16px;
}

.sel-index {
  width: 22px;
  height: 22px;
  border-radius: 4px;
  background-color: $aiq-color-black;
  color: $aiq-color-white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

.sel-field-info {
  min-width: 0;
}

.sel-field-name {
  font-size: 14px;
  font-weight: 600;
}

.sel-field-sub {
  font-size: 12px;
  color: $aiq-color-text-secondary;
  margin-top: 2px;
}

.sel-saving {
  margin-left: auto;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: $aiq-color-text-secondary;
}

.add-entity {
  margin-bottom: 12px;

  :deep(.el-select) {
    width: 100%;
  }
}

.mapping-hint {
  font-size: 12px;
  color: $aiq-color-text-secondary;
  margin-bottom: 10px;
}

.empty {
  color: $aiq-color-text-secondary;
  font-size: 13px;
  padding: 16px;
  text-align: center;
}

.order-index {
  width: 22px;
  height: 22px;
  border-radius: 4px;
  background-color: $aiq-color-black;
  color: $aiq-color-white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;

  &.muted {
    background-color: $aiq-input-border-hover;
  }
}

.map-item {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
  min-height: 40px;
  gap: 10px;
  background-color: $aiq-tab-content-background;
  border: 1px solid $aiq-tab-content-background;
  padding: 6px 10px;
  border-radius: 5px;

  &.is-shared {
    border-color: $aiq-color-warning;
  }
}

.map-body {
  min-width: 0;
  flex: 1;
}

.shared-note {
  font-size: 11px;
  color: $aiq-color-warning;
  margin-top: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.field-name {
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.drag-handle {
  cursor: grab;
  color: $aiq-input-border-hover;
}

.remove-icon {
  cursor: pointer;
  margin-left: auto;
  color: $aiq-input-border-hover;

  &:hover {
    color: $aiq-color-error;
  }
}

.suggested {
  margin-top: 16px;
  padding-top: 12px;
  border-top: 1px solid $aiq-tab-content-border;
}

.sug-title {
  font-size: 13px;
  font-weight: 600;
  color: $aiq-color-text-secondary;
  margin-bottom: 8px;
}

.sug-item {
  display: flex;
  align-items: center;
  gap: 10px;
  height: 34px;
  padding: 0 10px;
  margin-bottom: 6px;
  border: 1px dashed $aiq-icon-primary;
  border-radius: 5px;
  color: $aiq-color-text-secondary;

  .field-name {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .accept-btn {
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
    color: $aiq-bgc-button-primary;
    white-space: nowrap;

    &:hover {
      text-decoration: underline;
    }
  }

  .accepted-note {
    font-size: 11px;
    color: $aiq-color-success;
  }
}
</style>
